import * as THREE from 'three';
import { SKILLS } from '../game/config';
import type { Abilities } from '../game/Abilities';

/** The skills' glyphs (48×48), shared with the collection. */
export const SKILL_GLYPHS = [
  // meteor: a faceted rock trailing three flame tails
  '<path d="M5 9c7 3 14 8 21 15l-3 3C16 20 10 15 5 9z"/><path d="M12 3.5c5 4 10 8.5 15.5 14.5l-2.3 2.2C19.6 14.6 15.5 9.6 12 3.5z" opacity=".7"/><path d="M3.5 17c6 2 11 5 16 9.5l-2 2.2C13 24.5 8.6 21 3.5 17z" opacity=".55"/><path d="M24.5 25.5 33 20.5l8.5 5 1.5 9-6.5 7.5-9-.5-6.5-7.5z"/><path class="shine" d="m28 27 5-3 5.5 3-1 5.5-6.5 1z"/>',
  // ice wall: three crystals on a ledge
  '<path d="M4 40h40v4H4z"/><path d="M7 40V27l4.5-10 4.5 10v13z"/><path d="M17 40V19l6-14 6 14v21z"/><path d="M30 40V24l5-11 5 11v16z"/><path class="shine" d="m23 5 6 14h-6zM11.5 17l4.5 10h-4.5zM35 13l5 11h-5z"/>',
  // overload: a lightning bolt between two sparks
  '<path d="M29 2 11 27h11l-4.5 19L38 19H26.5z"/><path class="shine" d="M29 2 20 15h4z"/><path d="m39 6 2.5 3.5L39 13l-2.5-3.5zM9 34l2.5 3.5L9 41l-2.5-3.5z"/>',
  // frost wave: a bold six-armed snowflake
  '<g class="line" style="stroke:url(#skill-grad-3)"><path d="M24 4v40M6.7 14l34.6 20M6.7 34l34.6-20"/><path d="m18.5 7.5 5.5 5 5.5-5m-11 33 5.5-5 5.5 5M7.5 21.5l7.3 1.6-2.3 7.1m28.2-8.7-7.3 1.6 2.3 7.1M10.8 37.6l2.2-7.1-7.3-1.7m29.3 8.8-2.2-7.1 7.3-1.7"/></g><circle cx="24" cy="24" r="4.2"/>',
];
const _p = new THREE.Vector3(), _edge = new THREE.Vector3();
/** How to call off an aim: Esc on a keyboard; a finger taps the medallion again. */
const CANCEL = typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine)').matches ? 'Esc — скасувати' : 'ще раз на навичку — скасувати';

/**
 * A skill's medallion (index.html's .skill-* styles), shared with the collection: engraved ticks round
 * the bronze bezel, a charge ring in the skill's colour, then the well, the turning aura, the glyph and
 * the cooldown. The host sets --skill and --size.
 */
export function medallion(index: number): string {
  const ticks = Array.from({ length: 24 }, (_, k) => { const a = k / 24 * Math.PI * 2, r0 = k % 6 ? 45.5 : 44, r1 = 48.5;
    return `<line x1="${(50 + Math.cos(a) * r0).toFixed(1)}" y1="${(50 + Math.sin(a) * r0).toFixed(1)}" x2="${(50 + Math.cos(a) * r1).toFixed(1)}" y2="${(50 + Math.sin(a) * r1).toFixed(1)}"/>`; }).join('');
  return `<span class="skill-orb"><span class="skill-aura"></span>`
    + `<svg class="skill-glyph" viewBox="0 0 48 48" aria-hidden="true"><defs><linearGradient id="skill-grad-${index}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff"/><stop offset=".55" style="stop-color:color-mix(in srgb, var(--skill) 70%, #fff)"/><stop offset="1" style="stop-color:var(--skill)"/></linearGradient></defs><g class="skill-icon" fill="url(#skill-grad-${index})">${SKILL_GLYPHS[index]}</g></svg>`
    + `<span class="skill-cd"></span><span class="skill-time"></span>`
    + `<svg class="skill-dial" viewBox="0 0 100 100" aria-hidden="true"><g class="skill-ticks">${ticks}</g><circle class="skill-track" cx="50" cy="50" r="41.5"/><circle class="skill-charge" cx="50" cy="50" r="41.5" pathLength="100"/></svg>`
    + `</span>`;
}

/**
 * The skill medallion anchored by the seal: one active skill per battle (the lobby's pick), so one
 * medallion — the other three stay in the page, hidden, for the moment the pick is taken. No per-frame
 * layout reads.
 */
export class SkillBar {
  readonly buttons: HTMLButtonElement[] = [];
  private readonly hint: HTMLDivElement;
  private timer = 0;
  private width = innerWidth; private height = innerHeight;
  private lastHint = '';
  private readonly wasCooling = [false, false, false, false];
  private compact = false;
  private readonly bar: HTMLDivElement;

  constructor(host: HTMLElement, private readonly skills: Abilities, private readonly sealZ: number) {
    const bar = this.bar = document.createElement('div'); bar.className = 'skill-bar'; bar.setAttribute('role', 'group'); bar.setAttribute('aria-label', 'Бойові навички');
    SKILLS.forEach((skill, index) => {
      const button = document.createElement('button'); button.className = 'skill-button'; button.type = 'button'; button.dataset.skill = skill.id;
      button.style.setProperty('--skill', '#' + skill.color.toString(16).padStart(6,'0'));
      button.title = `${skill.name} · ${index + 1}\n${skill.detail}`;
      button.setAttribute('aria-label', `${skill.name}. ${skill.detail}`);
      button.innerHTML = medallion(index) + `<span class="skill-key">1</span><span class="skill-name">${skill.name}</span>`;
      button.addEventListener('pointerdown', e => e.stopPropagation());
      button.addEventListener('click', e => { e.stopPropagation(); this.skills.activate(index); this.timer = 0; });
      bar.append(button); this.buttons.push(button);
    });
    this.hint = document.createElement('div'); this.hint.className = 'skill-hint'; this.hint.setAttribute('role','status');
    this.hint.hidden = true;
    bar.append(this.hint); host.append(bar);
    // the field's own box: in Telegram it is inset from the window by the safe areas
    const size = () => { this.width = host.clientWidth || innerWidth; this.height = host.clientHeight || innerHeight; };
    size(); new ResizeObserver(size).observe(host);
    window.addEventListener('keydown', e => {
      if (e.repeat || e.ctrlKey || e.metaKey || e.altKey || /INPUT|TEXTAREA|SELECT/.test((e.target as HTMLElement)?.tagName)) return;
      if (e.key === 'Escape') this.skills.cancel();
      // one skill: 1 (or Q) casts it
      if (e.key === '1' || e.code === 'KeyQ') { e.preventDefault(); this.skills.activate(); }
      this.timer = 0;
    });
  }

  update(camera: THREE.Camera, dt: number) {
    this.timer -= dt; const status = this.timer <= 0; if (status) this.timer = 0.1;
    const compact = this.width < 560;
    if (compact !== this.compact) { this.compact = compact; this.bar.classList.toggle('compact', compact); }
    _p.set(-2.275,0.3,this.sealZ).project(camera);
    const anchorX=(_p.x+1)*this.width/2,anchorY=(1-_p.y)*this.height/2;
    const on = this.skills.activeIndex;
    this.buttons.forEach((button,i) => {
      // the one active skill stands where the four did, a size larger; the others wait hidden
      if (button.hidden !== (i !== on)) button.hidden = i !== on;
      if (i !== on) return;
      const x = -2.275, z = this.sealZ;
      _p.set(x,0.3,z).project(camera); _edge.set(x+0.42,0.3,z).project(camera);
      const size = compact ? 72 : Math.max(56,Math.min(88,Math.abs(_edge.x-_p.x)*this.width));
      const left = Math.round(compact ? anchorX : (_p.x+1)*this.width/2);
      const top = Math.round(compact ? anchorY+8 : (1-_p.y)*this.height/2);
      const position = `${left}px,${top}px`;
      if(button.dataset.position !== position) {button.style.left=left+'px';button.style.top=top+'px';button.dataset.position=position;}
      const rounded = Math.round(size)+'px'; if(button.style.getPropertyValue('--size')!==rounded)button.style.setProperty('--size',rounded);
      if(!status)return;
      const skill=SKILLS[i],remaining=this.skills.cooldown[i],active=this.skills.targeting===skill.id || (skill.id==='haste' && this.skills.hasteT>0);
      button.disabled=remaining>0; button.classList.toggle('active',active); button.setAttribute('aria-pressed',String(active));
      const seconds=remaining>0?String(Math.ceil(remaining)):'';
      const time=button.querySelector('.skill-time')!;if(time.textContent!==seconds)time.textContent=seconds;
      const cd=(Math.round(remaining/skill.cooldown*200)/200).toString();
      if(button.style.getPropertyValue('--cd')!==cd){button.style.setProperty('--cd',cd);
        button.querySelector<SVGCircleElement>('.skill-charge')!.style.strokeDasharray=`${(100-Number(cd)*100).toFixed(1)} 100`;}
      // a short flare the moment the skill comes back
      if(this.wasCooling[i]&&remaining<=0){button.classList.remove('ready');void button.offsetWidth;button.classList.add('ready');}
      this.wasCooling[i]=remaining>0;
    });
    const target = this.skills.targeting ? SKILLS.find(s => s.id === this.skills.targeting) : null;
    const denied = this.skills.denied ? SKILLS.find(s => s.id === this.skills.denied!.skill) : null;
    const hint = target ? `${target.name}: виберіть ${target.id === 'frost' ? 'область' : 'місце'} на дорозі · ${CANCEL}`
      : denied ? `${denied.name}: ${this.skills.denied!.reason === 'cooldown' ? 'ще заряджається' : 'ціль поза дорогою'}` : '';
    if(hint!==this.lastHint){this.hint.textContent=hint;this.hint.hidden=!hint;this.lastHint=hint;}
  }
}
